import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Modal,
  TextInput,
  Alert,
  Image,
  RefreshControl,
  Platform,
} from 'react-native';
// Trigger redeploy
import { router } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useRacha } from '@/contexts/RachaContext';
import { useGrupos } from '@/contexts/GruposContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Plus, Users, Calendar, Trash2, SquareCheck as CheckSquare, RotateCcw, CircleCheck as CheckCircle, Edit, TrendingUp, TrendingDown, Lock } from 'lucide-react-native';
import LoadingSpinner from '@/components/LoadingSpinner';
import SkeletonCard from '@/components/SkeletonCard';
import SearchFilter from '@/components/SearchFilter';
import BannerMenor from '@/components/BannerMenor';
import AdSenseBanner from '@/components/AdSenseBanner';
import { LimiteRachasModal } from '@/components/LimiteRachasModal';
import { formatarData } from '@/utils/formatarData';

export default function HomeScreen() {
  const { user, isPremium } = useAuth();
  const { rachasAtivos, rachasFinalizados, createRacha, updateRacha, deleteRacha, finalizarRacha, reativarRacha, getTotalDespesas, showLimiteModal, setShowLimiteModal, limiteRachas } = useRacha();
  const { grupos, getAllParticipantes } = useGrupos();
  const { colors } = useTheme();

  const [activeTab, setActiveTab] = useState<'ativos' | 'finalizados'>('ativos');

  const [showCreateModal, setShowCreateModal] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showSelectParticipantesModal, setShowSelectParticipantesModal] = useState(false);
  const [editingRacha, setEditingRacha] = useState<any>(null);

  const [novoRachaNome, setNovoRachaNome] = useState('');
  const [novoRachaEmoji, setNovoRachaEmoji] = useState('💰');
  const [participantesSelecionados, setParticipantesSelecionados] = useState<string[]>([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState<string>('');

  // Estados para loading e feedback
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Estados para busca e filtros
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [sortBy] = useState<'nome' | 'data' | 'valor'>('data');

  const todosParticipantes = getAllParticipantes();

  // Simular carregamento inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Função de refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simular refresh dos dados
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsRefreshing(false);
  };

  // Funções de busca e filtro
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilter = (filter: string) => {
    setActiveFilter(filter);
  };

  // Filtrar e ordenar rachas
  const getRachasFiltrados = () => {
    let rachas = activeTab === 'ativos' ? rachasAtivos : rachasFinalizados;

    // Aplicar busca
    if (searchQuery.trim()) {
      rachas = rachas.filter(racha =>
        racha.nome.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Aplicar filtro
    if (activeFilter) {
      if (activeFilter === 'meus') {
        rachas = rachas.filter(racha => racha.criadorId === user?.id);
      } else if (activeFilter === 'participando') {
        rachas = rachas.filter(racha => racha.participantes.some(p => p.id === user?.id));
      }
    }

    // Aplicar ordenação
    rachas.sort((a, b) => {
      switch (sortBy) {
        case 'nome':
          return a.nome.localeCompare(b.nome);
        case 'valor':
          return getTotalDespesas(b.id) - getTotalDespesas(a.id);
        case 'data':
        default:
          return new Date(b.ultimaAtividade).getTime() - new Date(a.ultimaAtividade).getTime();
      }
    });

    return rachas;
  };

  const rachasParaExibir = getRachasFiltrados();

  // Estados para alertas nativos
  const [showNativeAlert, setShowNativeAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    message: string;
    onConfirm?: () => void | Promise<void>;
    showCancel?: boolean;
  }>({ title: '', message: '' });

  // Função de alert que funciona no web e mobile
  const showAlert = (title: string, message: string, onConfirm?: () => void, showCancel: boolean = true) => {
    if (typeof window !== 'undefined' && !showCancel) {
      // Alerta informativo simples no web
      window.alert(`${title}\n\n${message}`);
      if (onConfirm) onConfirm();
      return;
    }
    setAlertConfig({ title, message, onConfirm, showCancel });
    setShowNativeAlert(true);
  };

  const formatarSaldo = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Math.abs(valor));
  };

  const calcularSaldoUsuario = (racha: any) => {
    // 1. Encontrar o grupoDeBolsoId do usuário (se houver)
    const usuarioParticipante = racha.participantes?.find((p: any) => p.id === user?.id);
    const grupoDeBolsoId = usuarioParticipante?.grupoDeBolsoId;

    // 2. Se estiver em um grupo de bolso, calcular saldo do grupo inteiro
    const idsNoMesmoBolso = grupoDeBolsoId
      ? racha.participantes
        .filter((p: any) => p.grupoDeBolsoId === grupoDeBolsoId)
        .map((p: any) => p.id)
      : [user?.id];

    let saldo = 0;

    racha.despesas.forEach((despesa: any) => {
      // Somar pagamentos de qualquer membro do bolso
      if (idsNoMesmoBolso.includes(despesa.pagoPorId)) {
        saldo += despesa.valor;
      }

      // Subtrair divisões de qualquer membro do bolso
      despesa.divisoes.forEach((d: any) => {
        if (idsNoMesmoBolso.includes(d.usuarioId)) {
          saldo -= d.valorDevido;
        }
      });
    });

    return saldo;
  };

  const obterCorSaldo = (valor: number) => {
    if (valor > 0) return colors.success;
    if (valor < 0) return colors.warning;
    return colors.textSecondary;
  };

  const obterTextoSaldo = (valor: number) => {
    if (valor > 0) return `Te devem ${formatarSaldo(valor)}`;
    if (valor < 0) return `Você deve ${formatarSaldo(valor)}`;
    return 'Quitado';
  };

  const criarRacha = async () => {
    if (!novoRachaNome.trim()) {
      Alert.alert('Erro', 'Digite um nome para o racha');
      return;
    }

    // Verificar limite antes de tentar criar
    if (!isPremium() && rachasAtivos.length >= limiteRachas) {
      setShowLimiteModal(true);
      return;
    }

    setIsLoading(true);
    try {
      let participantesParaRacha: any[] = [];

      if (grupoSelecionado) {
        const grupo = grupos.find(g => g.id === grupoSelecionado);
        if (grupo) {
          participantesParaRacha = grupo.participantes;
        }
      } else if (participantesSelecionados.length > 0) {
        participantesParaRacha = todosParticipantes.filter(p =>
          participantesSelecionados.includes(p.id)
        );
      }

      const rachaId = await createRacha(novoRachaNome.trim(), novoRachaEmoji, participantesParaRacha);

      if (!rachaId) {
        throw new Error('Falha ao criar racha - ID não retornado');
      }

      // Limpar formulário
      setNovoRachaNome('');
      setNovoRachaEmoji('💰');
      setParticipantesSelecionados([]);
      setGrupoSelecionado('');
      setShowCreateModal(false);
      setShowSelectParticipantesModal(false);

      router.push(`/rachas/${rachaId}`);
    } catch (error: any) {
      if (error.message === 'Limite de rachas atingido') {
        Alert.alert(
          'Limite Atingido',
          'Usuários gratuitos podem ter até 3 rachas ativos. Assine o plano premium para rachas ilimitados!',
          [
            { text: 'Cancelar', style: 'cancel' },
            { text: 'Assinar', onPress: () => router.navigate('/assinatura') }
          ]
        );
      } else {
        Alert.alert('Erro', 'Falha ao criar racha');
      }
    }
    finally {
      setIsLoading(false);
    }
  };

  const editarRacha = async () => {
    if (!novoRachaNome.trim()) {
      Alert.alert('Erro', 'Digite um nome para o racha');
      return;
    }

    try {
      await updateRacha(editingRacha.id, {
        nome: novoRachaNome.trim(),
        emoji: novoRachaEmoji
      });
      setNovoRachaNome('');
      setNovoRachaEmoji('💰');
      setEditingRacha(null);
      setShowEditModal(false);
    } catch {
      Alert.alert('Erro', 'Falha ao editar racha');
    }
  };

  const confirmarExclusao = (racha: any) => {
    showAlert(
      'Excluir Racha',
      `Tem certeza que deseja excluir o racha "${racha.nome}"? Esta ação não pode ser desfeita.`,
      () => {
        deleteRacha(racha.id);
        showAlert('Sucesso', 'Racha excluído com sucesso!', undefined, false);
      }
    );
  };

  const confirmarFinalizacao = (racha: any) => {
    showAlert(
      'Finalizar Racha',
      `Tem certeza que deseja finalizar o racha "${racha.nome}"? Após finalizado, não será possível adicionar novas despesas nem reativar o racha.`,
      async () => {
        await finalizarRacha(racha.id);
      }
    );
  };

  const confirmarReativacao = (racha: any) => {
    showAlert(
      'Reativar Racha',
      `Tem certeza que deseja reativar o racha "${racha.nome}"?`,
      async () => {
        await reativarRacha(racha.id);
      }
    );
  };


  const abrirEdicao = (racha: any) => {
    setEditingRacha(racha);
    setNovoRachaNome(racha.nome);
    setNovoRachaEmoji(racha.emoji);
    setShowEditModal(true);
  };

  const abrirRacha = (rachaId: string) => {
    router.push(`/rachas/${rachaId}`);
  };

  const toggleParticipante = (participanteId: string) => {
    // Se há um grupo selecionado, desselecionar automaticamente
    if (grupoSelecionado) {
      setGrupoSelecionado('');
      // Feedback visual sutil
      showAlert('Grupo desmarcado', 'O grupo foi automaticamente desmarcado para permitir seleção individual de participantes.', undefined, false);
    }

    setParticipantesSelecionados(prev => {
      if (prev.includes(participanteId)) {
        return prev.filter(id => id !== participanteId);
      } else {
        return [...prev, participanteId];
      }
    });
  };

  const selecionarGrupo = (grupoId: string) => {
    setGrupoSelecionado(grupoId);
    setParticipantesSelecionados([]);
  };

  const desfazerSelecaoGrupo = () => {
    setGrupoSelecionado('');
    setParticipantesSelecionados([]);
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      paddingHorizontal: 24,
      paddingVertical: 16,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    greetingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    greeting: {
      fontSize: 22,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 4,
    },
    premiumIndicator: {
      marginLeft: 8,
      justifyContent: 'center',
      alignItems: 'center',
    },
    subtitle: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    tabs: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    tab: {
      flex: 1,
      paddingVertical: 16,
      alignItems: 'center',
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
    },
    activeTab: {
      borderBottomColor: colors.primary,
    },
    tabText: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.textSecondary,
    },
    activeTabText: {
      color: colors.primary,
    },
    content: {
      flex: 1,
      paddingHorizontal: 24,
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 80,
    },
    emptyTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 8,
    },
    emptySubtitle: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 20,
    },
    rachasContainer: {
      paddingVertical: 16,
      gap: 12,
    },
    rachaCard: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: colors.border,
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 3,
    },
    rachaCardFinalizado: {
      opacity: 0.7,
    },
    rachaHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      minHeight: 80, // Garantir altura mínima para evitar sobreposição
    },
    rachaInfo: {
      flex: 1,
      gap: 6,
    },
    rachaHeaderRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 4,
    },
    tituloIconeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 4,
    },
    rachaData: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    rachaEmoji: {
      fontSize: 28,
    },
    rachaNome: {
      fontSize: 16,
      fontFamily: 'Inter-Bold',
      color: colors.text,
      flex: 1,
    },
    metadataRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginBottom: 2,
    },
    participantesTexto: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },


    rachaActions: {
      flexDirection: 'row',
      gap: 8,
      alignItems: 'flex-start',
      marginTop: 4, // Pequeno espaçamento do topo
    },
    actionButton: {
      padding: 8,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    saldoTexto: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
    },
    rachaFooter: {
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: 12,
    },
    ultimaAtividade: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    ultimaAtividadeTexto: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    fab: {
      position: 'absolute',
      bottom: 24,
      right: 24,
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
      elevation: 8,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      zIndex: 9999,
    },
    modalContainer: {
      flex: 1,
      backgroundColor: colors.background,
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 24,
      paddingVertical: 16,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    modalCancelButton: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    modalTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    modalCreateButton: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    modalContent: {
      flex: 1,
      paddingHorizontal: 24,
      paddingTop: 24,
    },
    emojiSelector: {
      marginBottom: 24,
    },
    selectedEmoji: {
      fontSize: 64,
      textAlign: 'center',
      marginBottom: 16,
    },
    emojiList: {
      flexDirection: 'row',
      gap: 8,
      paddingHorizontal: 8,
    },
    emojiOption: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: colors.surface,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 2,
      borderColor: 'transparent',
    },
    emojiSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    emojiOptionText: {
      fontSize: 20,
    },
    inputContainer: {
      gap: 16,
    },
    inputLabel: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 8,
    },
    nomeInput: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      paddingHorizontal: 16,
      height: 56,
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      borderWidth: 1,
      borderColor: colors.border,
    },
    participantesSection: {
      marginTop: 24,
    },
    sectionTitle: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 16,
    },
    gruposList: {
      gap: 12,
      marginBottom: 24,
    },
    grupoOption: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      borderWidth: 2,
      borderColor: colors.border,
      gap: 12,
    },
    grupoSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    grupoEmoji: {
      fontSize: 24,
    },
    grupoInfo: {
      flex: 1,
    },
    grupoNome: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 2,
    },
    grupoParticipantes: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    radioButton: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: colors.border,
      justifyContent: 'center',
      alignItems: 'center',
    },
    radioButtonSelected: {
      borderColor: colors.primary,
    },
    radioButtonInner: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: colors.primary,
    },
    divider: {
      height: 1,
      backgroundColor: colors.border,
      marginVertical: 16,
    },
    dividerText: {
      textAlign: 'center',
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      backgroundColor: colors.background,
      paddingHorizontal: 16,
      marginTop: -8,
    },
    participantesList: {
      gap: 8,
    },
    participanteOption: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      borderWidth: 1,
      borderColor: colors.border,
      gap: 12,
    },
    participanteSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    participanteFoto: {
      width: 32,
      height: 32,
      borderRadius: 16,
    },
    participanteNome: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
      flex: 1,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: colors.border,
      justifyContent: 'center',
      alignItems: 'center',
    },
    checkboxSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary,
    },
    checkmark: {
      width: 8,
      height: 8,
      borderRadius: 2,
      backgroundColor: '#FFFFFF',
    },
    nextButton: {
      backgroundColor: colors.primary,
      borderRadius: 12,
      paddingVertical: 16,
      alignItems: 'center',
      marginTop: 24,
    },
    nextButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#FFFFFF',
    },
    desfazerButton: {
      padding: 16,
      borderRadius: 8,
      backgroundColor: colors.background,
      alignItems: 'center',
    },
    desfazerButtonText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    participanteDisabled: {
      color: colors.textSecondary,
    },
    alertOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      elevation: 9999,
    },
    alertContainer: {
      backgroundColor: colors.surface,
      padding: 24,
      borderRadius: 16,
      width: '80%',
      maxHeight: '80%',
    },
    alertTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 16,
    },
    alertMessage: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginBottom: 24,
    },
    alertButtons: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    alertButton: {
      padding: 12,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    alertButtonCancel: {
      backgroundColor: colors.error,
    },
    alertButtonCancelText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#FFFFFF',
    },
    alertButtonConfirm: {
      backgroundColor: colors.success,
    },
    alertButtonConfirmText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#FFFFFF',
    },
    // Estilos para o informativo
    infoCard: {
      backgroundColor: colors.primary + '10',
      borderRadius: 12,
      padding: 16,
      marginBottom: 20,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    infoIcon: {
      marginBottom: 8,
    },
    infoContent: {
      flex: 1,
    },
    infoTitle: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 8,
    },
    infoText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 8,
    },
    infoHighlight: {
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    infoNote: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      fontStyle: 'italic',
      lineHeight: 16,
    },
  });

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <View style={styles.greetingContainer}>
          <Text style={styles.greeting}>Olá, {user?.nome?.split(' ')[0]}! 👋</Text>
          {isPremium() && (
            <View style={styles.premiumIndicator}>
              <Image 
                source={require('@/assets/images/crown.png')} 
                style={{ width: 42, height: 42, backgroundColor: 'transparent' }} 
                resizeMode="contain" 
              />
            </View>
          )}
        </View>
        <Text style={styles.subtitle}>Gerencie seus rachas</Text>
      </View>

      {/* Banner menor para usuários gratuitos */}
      <BannerMenor />

      {Platform.OS === 'web' && !isPremium() ? (
        <View style={{ marginHorizontal: 16, marginBottom: 8, height: 90, maxHeight: 90, overflow: 'hidden' as any }}>
          <AdSenseBanner
            adSlot="2112588249"
            adKey="tabs-rachas-cta"
            format="horizontal"
            fullWidthResponsive={false}
            style={{ height: 90, maxHeight: 90 }}
          />
        </View>
      ) : null}

      <View style={styles.tabs}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'ativos' && styles.activeTab]}
          onPress={() => setActiveTab('ativos')}
        >
          <Text style={[styles.tabText, activeTab === 'ativos' && styles.activeTabText]}>
            Ativos ({rachasAtivos.length})
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, activeTab === 'finalizados' && styles.activeTab]}
          onPress={() => setActiveTab('finalizados')}
        >
          <Text style={[styles.tabText, activeTab === 'finalizados' && styles.activeTabText]}>
            Finalizados ({rachasFinalizados.length})
          </Text>
        </TouchableOpacity>
      </View>

      <SearchFilter
        onSearch={handleSearch}
        onFilter={handleFilter}
        searchPlaceholder="Buscar rachas..."
        filterOptions={[
          { label: 'Meus Rachas', value: 'meus' },
          { label: 'Participando', value: 'participando' },
        ]}
        activeFilter={activeFilter}
      />

      <ScrollView
        style={styles.content}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
      >
        {isInitialLoading ? (
          // Skeleton loading
          <View style={styles.rachasContainer}>
            {[1, 2, 3].map((i) => (
              <View key={i} style={styles.rachaCard}>
                <SkeletonCard height={80} style={{ marginBottom: 12 }} />
                <SkeletonCard height={20} width="60%" />
              </View>
            ))}
          </View>
        ) : rachasParaExibir.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>
              {activeTab === 'ativos' ? 'Nenhum racha ativo' : 'Nenhum racha finalizado'}
            </Text>
            <Text style={styles.emptySubtitle}>
              {activeTab === 'ativos'
                ? 'Crie seu primeiro racha e comece a dividir despesas!'
                : 'Rachas finalizados aparecerão aqui'
              }
            </Text>
          </View>
        ) : (
          <View style={styles.rachasContainer}>
            {rachasParaExibir.map((racha) => {
              const saldoUsuario = calcularSaldoUsuario(racha);
              return (
                <TouchableOpacity
                  key={racha.id}
                  style={[
                    styles.rachaCard,
                    racha.status === 'finalizado' && styles.rachaCardFinalizado
                  ]}
                  onPress={() => abrirRacha(racha.id)}
                  activeOpacity={0.7}
                >
                  <View style={styles.rachaHeader}>
                    {/* Informações principais */}
                    <View style={styles.rachaInfo}>
                      {/* Linha 1: Título e ícone agrupados */}
                      <View style={styles.tituloIconeRow}>
                        <Text style={styles.rachaEmoji}>{racha.emoji}</Text>
                        <Text style={styles.rachaNome}>
                          {racha.nome}
                        </Text>
                      </View>

                      {/* Linha 2: Participantes */}
                      <View style={styles.metadataRow}>
                        <Users size={12} color={colors.textSecondary} />
                        <Text style={styles.participantesTexto}>
                          {racha.participantes.length} participantes
                        </Text>
                      </View>

                      {/* Linha 3: Total */}
                      <View style={styles.metadataRow}>
                        <Text style={styles.participantesTexto}>💰</Text>
                        <Text style={styles.participantesTexto}>
                          Total: {formatarSaldo(getTotalDespesas(racha.id))}
                        </Text>
                      </View>

                      {/* Linha 4: Balanço */}
                      <View style={styles.metadataRow}>
                        {calcularSaldoUsuario(racha) >= 0 ? (
                          <TrendingUp size={12} color={obterCorSaldo(calcularSaldoUsuario(racha))} />
                        ) : (
                          <TrendingDown size={12} color={obterCorSaldo(calcularSaldoUsuario(racha))} />
                        )}
                        <Text style={[styles.saldoTexto, { color: obterCorSaldo(calcularSaldoUsuario(racha)) }]}>
                          {obterTextoSaldo(calcularSaldoUsuario(racha))}
                        </Text>
                      </View>

                      {/* Linha 5: Data */}
                      <View style={styles.metadataRow}>
                        <Calendar size={12} color={colors.textSecondary} />
                        <Text style={styles.rachaData}>
                          {formatarData(racha.ultimaAtividade)}
                        </Text>
                      </View>
                    </View>

                    {racha.criadorId === user?.id && (
                      <View style={styles.rachaActions}>
                        {racha.status === 'ativo' ? (
                          <>
                            <TouchableOpacity
                              style={styles.actionButton}
                              onPress={(e) => {
                                e.stopPropagation();
                                abrirEdicao(racha);
                              }}
                              activeOpacity={0.7}
                            >
                              <Edit size={16} color={colors.primary} />
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={styles.actionButton}
                              onPress={(e) => {
                                e.stopPropagation();
                                confirmarFinalizacao(racha);
                              }}
                              activeOpacity={0.7}
                            >
                              <CheckSquare size={16} color={colors.success} />
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={styles.actionButton}
                              onPress={(e) => {
                                e.stopPropagation();
                                confirmarExclusao(racha);
                              }}
                              activeOpacity={0.7}
                            >
                              <Trash2 size={16} color={colors.error} />
                            </TouchableOpacity>
                          </>
                        ) : (
                          <>
                            <TouchableOpacity
                              style={styles.actionButton}
                              onPress={(e) => {
                                e.stopPropagation();
                                if (user?.plano === 'vitalicio' || user?.plano === 'anual') {
                                  setAlertConfig({
                                    title: 'Reabrir Racha?',
                                    message: 'Ao reabrir, o racha voltará para a aba "Ativos".',
                                    showCancel: true,
                                    onConfirm: async () => {
                                      await reativarRacha(racha.id);
                                    }
                                  });
                                  setShowNativeAlert(true);
                                } else {
                                  Alert.alert(
                                    'Recurso Premium',
                                    'A reabertura de rachas é exclusiva para usuários Premium.',
                                    [
                                      { text: 'Cancelar', style: 'cancel' },
                                      { text: 'Saiba mais', onPress: () => router.push('/assinatura') }
                                    ]
                                  );
                                }
                              }}
                              activeOpacity={0.7}
                            >
                              {(user?.plano === 'vitalicio' || user?.plano === 'anual') ? (
                                <RotateCcw size={16} color={colors.primary} />
                              ) : (
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                  <RotateCcw size={16} color={colors.textSecondary} />
                                  <Lock size={10} color={colors.textSecondary} />
                                </View>
                              )}
                            </TouchableOpacity>

                            <TouchableOpacity
                              style={styles.actionButton}
                              onPress={(e) => {
                                e.stopPropagation();
                                confirmarExclusao(racha);
                              }}
                              activeOpacity={0.7}
                            >
                              <Trash2 size={16} color={colors.error} />
                            </TouchableOpacity>
                          </>
                        )}
                      </View>
                    )}
                  </View>

                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </ScrollView>

      {activeTab === 'ativos' && (
        <TouchableOpacity
          style={styles.fab}
          onPress={() => {
            setShowCreateModal(true);
          }}
          activeOpacity={0.8}
        >
          <Plus size={24} color="#FFFFFF" />
        </TouchableOpacity>
      )}

      {/* Modal Criar Racha - Passo 1 */}
      <Modal
        visible={showCreateModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowCreateModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowCreateModal(false)}>
              <Text style={styles.modalCancelButton}>Cancelar</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Novo Racha</Text>
            <TouchableOpacity onPress={() => {
              if (novoRachaNome.trim()) {
                setShowSelectParticipantesModal(true);
              } else {
                showAlert('Erro', 'Digite um nome para o racha', undefined, false);
              }
            }}>
              <Text style={styles.modalCreateButton}>Avançar</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.modalContent}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <View style={styles.emojiSelector}>
                  <Text style={styles.selectedEmoji}>{novoRachaEmoji}</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View style={styles.emojiList}>
                      {['💰', '🏖️', '🍕', '🏠', '🚗', '🎉', '🍻', '✈️', '🎬', '🏋️'].map((emoji) => (
                        <TouchableOpacity
                          key={emoji}
                          style={[
                            styles.emojiOption,
                            novoRachaEmoji === emoji && styles.emojiSelected
                          ]}
                          onPress={() => setNovoRachaEmoji(emoji)}
                        >
                          <Text style={styles.emojiOptionText}>{emoji}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </ScrollView>
                </View>

                <View style={styles.inputContainer}>
                  <Text style={styles.inputLabel}>Nome do racha</Text>
                  <TextInput
                    style={styles.nomeInput}
                    placeholder="Ex: Viagem à praia"
                    placeholderTextColor={colors.textSecondary}
                    value={novoRachaNome}
                    onChangeText={setNovoRachaNome}
                    maxLength={30}
                  />
                </View>
              </>
            )}
          </View>
        </SafeAreaView>
      </Modal>

      {/* Modal Selecionar Participantes - Passo 2 */}
      <Modal
        visible={showSelectParticipantesModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowSelectParticipantesModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowSelectParticipantesModal(false)}>
              <Text style={styles.modalCancelButton}>Voltar</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Participantes</Text>
            <TouchableOpacity onPress={() => {
              criarRacha();
            }}>
              <Text style={styles.modalCreateButton}>Criar</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                {/* Informativo intuitivo */}
                <View style={styles.infoCard}>
                  <View style={styles.infoIcon}>
                    <Users size={20} color={colors.primary} />
                  </View>
                  <View style={styles.infoContent}>
                    <Text style={styles.infoTitle}>Como adicionar participantes?</Text>
                    <Text style={styles.infoText}>
                      Você pode adicionar participantes de três formas:{'\n'}
                      • <Text style={styles.infoHighlight}>Selecionar um grupo</Text> - Adiciona todos os participantes do grupo criado na aba Grupos{'\n'}
                      • <Text style={styles.infoHighlight}>Selecionar individualmente</Text> - Escolhe participantes específicos criados na aba Grupos{'\n'}
                      • <Text style={styles.infoHighlight}>Após a criação do racha</Text> - Adicione participantes individualmente após a criação do racha
                    </Text>
                    <Text style={styles.infoNote}>
                      💡 Dica: Selecionar participantes individuais automaticamente desmarca qualquer grupo selecionado.
                    </Text>
                  </View>
                </View>

                <View style={styles.participantesSection}>
                  <Text style={styles.sectionTitle}>Selecionar grupo existente</Text>

                  {grupos.length > 0 ? (
                    <View style={styles.gruposList}>
                      {grupos.map((grupo) => (
                        <TouchableOpacity
                          key={grupo.id}
                          style={[
                            styles.grupoOption,
                            grupoSelecionado === grupo.id && styles.grupoSelected
                          ]}
                          onPress={() => selecionarGrupo(grupo.id)}
                        >
                          <Text style={styles.grupoEmoji}>{grupo.emoji}</Text>
                          <View style={styles.grupoInfo}>
                            <Text style={styles.grupoNome}>{grupo.nome}</Text>
                            <Text style={styles.grupoParticipantes}>
                              {grupo.participantes.length} participantes
                            </Text>
                          </View>
                          <View style={[
                            styles.radioButton,
                            grupoSelecionado === grupo.id && styles.radioButtonSelected
                          ]}>
                            {grupoSelecionado === grupo.id && (
                              <View style={styles.radioButtonInner} />
                            )}
                          </View>
                        </TouchableOpacity>
                      ))}

                      {grupoSelecionado && (
                        <TouchableOpacity
                          style={styles.desfazerButton}
                          onPress={() => {
                            desfazerSelecaoGrupo();
                          }}
                        >
                          <Text style={styles.desfazerButtonText}>Desfazer seleção de grupo</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  ) : (
                    <Text style={styles.emptySubtitle}>
                      Nenhum grupo criado. Vá para &quot;Meus Grupos&quot; para criar grupos de participantes.
                    </Text>
                  )}

                  {grupos.length > 0 && todosParticipantes.length > 0 && (
                    <>
                      <View style={styles.divider} />
                      <Text style={styles.dividerText}>OU</Text>
                    </>
                  )}

                  {todosParticipantes.length > 0 && (
                    <>
                      <Text style={styles.sectionTitle}>Selecionar participantes individuais</Text>

                      <View style={styles.participantesList}>
                        {todosParticipantes.map((participante) => (
                          <TouchableOpacity
                            key={participante.id}
                            style={[
                              styles.participanteOption,
                              participantesSelecionados.includes(participante.id) && styles.participanteSelected
                            ]}
                            onPress={() => toggleParticipante(participante.id)}
                          >
                            <Image source={{ uri: participante.foto }} style={styles.participanteFoto} />
                            <Text style={styles.participanteNome}>{participante.nome}</Text>
                            <View style={[
                              styles.checkbox,
                              participantesSelecionados.includes(participante.id) && styles.checkboxSelected
                            ]}>
                              {participantesSelecionados.includes(participante.id) && (
                                <CheckCircle size={12} color="#FFFFFF" />
                              )}
                            </View>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </>
                  )}

                  {grupos.length === 0 && todosParticipantes.length === 0 && (
                    <Text style={styles.emptySubtitle}>
                      Você será adicionado automaticamente ao racha. Para adicionar outros participantes,
                      crie grupos em &quot;Meus Grupos&quot; primeiro.
                    </Text>
                  )}
                </View>
              </>
            )}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Modal Editar Racha */}
      <Modal
        visible={showEditModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowEditModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowEditModal(false)}>
              <Text style={styles.modalCancelButton}>Cancelar</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Editar Racha</Text>
            <TouchableOpacity onPress={editarRacha}>
              <Text style={styles.modalCreateButton}>Salvar</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.modalContent}>
            <View style={styles.emojiSelector}>
              <Text style={styles.selectedEmoji}>{novoRachaEmoji}</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.emojiList}>
                  {['💰', '🏖️', '🍕', '🏠', '🚗', '🎉', '🍻', '✈️', '🎬', '🏋️'].map((emoji) => (
                    <TouchableOpacity
                      key={emoji}
                      style={[
                        styles.emojiOption,
                        novoRachaEmoji === emoji && styles.emojiSelected
                      ]}
                      onPress={() => setNovoRachaEmoji(emoji)}
                    >
                      <Text style={styles.emojiOptionText}>{emoji}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Nome do racha</Text>
              <TextInput
                style={styles.nomeInput}
                placeholder="Ex: Viagem à praia"
                placeholderTextColor={colors.textSecondary}
                value={novoRachaNome}
                onChangeText={setNovoRachaNome}
                maxLength={30}
              />
            </View>
          </View>
        </SafeAreaView>
      </Modal>

      {/* Alerta Nativo */}
      {showNativeAlert && (
        <View style={styles.alertOverlay}>
          <View style={styles.alertContainer}>
            <Text style={styles.alertTitle}>{alertConfig.title}</Text>
            <Text style={styles.alertMessage}>{alertConfig.message}</Text>
            <View style={styles.alertButtons}>
              {alertConfig.showCancel && (
                <TouchableOpacity
                  style={[styles.alertButton, styles.alertButtonCancel]}
                  onPress={() => setShowNativeAlert(false)}
                >
                  <Text style={styles.alertButtonCancelText}>Cancelar</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.alertButton, styles.alertButtonConfirm]}
                onPress={async () => {
                  setShowNativeAlert(false);
                  if (alertConfig.onConfirm) {
                    await alertConfig.onConfirm();
                  }
                }}
              >
                <Text style={styles.alertButtonConfirmText}>
                  {alertConfig.showCancel ? 'Confirmar' : 'OK'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Modal de Limite de Rachas */}
      <LimiteRachasModal
        visible={showLimiteModal}
        onClose={() => setShowLimiteModal(false)}
        onUpgrade={() => {
          setShowLimiteModal(false);
          // Use native navigation for web since expo-router's push/navigate fail silently for tabs
          setTimeout(() => {
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              window.location.href = '/assinatura';
            } else {
              router.push('/assinatura');
            }
          }, 100);
        }}
        limite={limiteRachas}
        atual={rachasAtivos.length}
      />

    </SafeAreaView>
  );
}
